import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getConsentRecord } from './ConsentNotice';
import { loadSangatiGroup } from '../lib/sangati';
import { useGlossary } from '../contexts/GlossaryContext';
import {
  completeJourney,
  markJourneyStep,
  startJourney,
  type JourneyProgress,
} from '../lib/journey';
import { searchLinguistics, type LinguisticMatch, type LinguisticSearchResult, type VocabularyContext } from '../lib/api';
import '../styles/JourneyPage.css';

interface JourneyPageProps {
  onNavigate: (path: string) => void;
}

type TranslateFn = ReturnType<typeof useGlossary>['t'];

function StepCard({
  title,
  body,
  done,
  actionLabel,
  onAction,
  muted,
  t,
}: {
  title: string;
  body: string;
  done: boolean;
  actionLabel: string;
  onAction: () => void;
  muted?: string;
  t: TranslateFn;
}) {
  return (
    <article className={`journey-step ${done ? 'journey-step--done' : ''}`}>
      <div className="journey-step-head">
        <h3>{title}</h3>
        <span className={`journey-step-pill ${done ? 'journey-step-pill--done' : ''}`}>
          {done ? t('journey.status.done') : t('journey.status.next')}
        </span>
      </div>
      <p>{body}</p>
      {muted && <div className="journey-step-muted">{muted}</div>}
      <button className="journey-step-btn" onClick={onAction}>{actionLabel}</button>
    </article>
  );
}

function MatchTile({ match, t }: { match: LinguisticMatch; t: TranslateFn }) {
  return (
    <article className="journey-match">
      <div className="journey-match-head">
        <strong>{match.term || match.title || t('journey.match.unknownTerm')}</strong>
        <span>{match.source}</span>
      </div>
      <div className="journey-match-meta">{t('journey.match.structure')}: {match.root_structure || '—'}</div>
      <div className="journey-match-meta">{t('journey.match.hindi')}: {match.hindi_meaning || match.meaning || '—'}</div>
      <div className="journey-match-meta">{t('journey.match.nirukti')}: {match.nirukti_explanation || '—'}</div>
      {match.summary && <p className="journey-match-summary">{match.summary}</p>}
      {match.contexts && match.contexts.length > 0 && (
        <div className="journey-match-contexts">
          <div className="journey-match-contexts-title">
            {t('journey.match.contexts')} ({match.contexts.length})
          </div>
          {match.contexts.map((context) => (
            <ContextCard key={context.id} context={context} t={t} />
          ))}
        </div>
      )}
    </article>
  );
}

function ContextCard({ context, t }: { context: VocabularyContext; t: TranslateFn }) {
  return (
    <div className="journey-match-context">
      <strong>{context.title}</strong>
      {context.description && <p>{context.description}</p>}
      {context.examples && context.examples.length > 0 && (
        <div className="journey-match-context-block">
          <div className="journey-match-context-label">{t('journey.match.examples')}</div>
          <ul>
            {context.examples.map((example, index) => (
              <li key={`${context.id}-example-${index}`}>{example}</li>
            ))}
          </ul>
        </div>
      )}
      {context.notes && (
        <div className="journey-match-context-block">
          <div className="journey-match-context-label">{t('journey.match.notes')}</div>
          {Array.isArray(context.notes) ? (
            context.notes.length > 0 && (
              <ul>
                {context.notes.map((note, index) => (
                  <li key={`${context.id}-note-${index}`}>{note}</li>
                ))}
              </ul>
            )
          ) : (
            Object.keys(context.notes).length > 0 && (
              <dl>
                {Object.entries(context.notes).map(([label, value]) => (
                  <div key={`${context.id}-${label}`}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            )
          )}
        </div>
      )}
    </div>
  );
}

function CorpusSection({
  t,
  query,
  searchResult,
  searchLoading,
  searchError,
  onQueryChange,
  onSearch,
}: {
  t: TranslateFn;
  query: string;
  searchResult: LinguisticSearchResult | null;
  searchLoading: boolean;
  searchError: string;
  onQueryChange: (value: string) => void;
  onSearch: (e?: FormEvent) => void;
}) {
  return (
    <section className="journey-corpus">
      <div className="journey-corpus-head">
        <div>
          <div className="journey-corpus-eyebrow">{t('journey.corpus.eyebrow')}</div>
          <h2>{t('journey.corpus.title')}</h2>
        </div>
        <div className="journey-corpus-count">
          {searchResult ? t('journey.corpus.count', { count: searchResult.count }) : '—'}
        </div>
      </div>
      <form className="journey-search" onSubmit={onSearch}>
        <input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={t('journey.corpus.placeholder')}
        />
        <button type="submit" className="journey-btn journey-btn--primary" disabled={searchLoading}>
          {searchLoading ? t('journey.search.loading') : t('journey.search.submit')}
        </button>
      </form>
      {searchError && <div className="journey-error">{searchError}</div>}
      <div className="journey-corpus-meta">
        {searchResult?.sources?.map((source) => (
          <span key={source.source}>{source.source}: {source.count}</span>
        ))}
      </div>
      <div className="journey-match-grid">
        {(searchResult?.matches ?? []).map((match) => <MatchTile key={`${match.source}-${match.path}`} match={match} t={t} />)}
        {!searchResult && !searchLoading && <p className="journey-empty">{t('journey.corpus.empty')}</p>}
        {searchResult && searchResult.matches.length === 0 && <p className="journey-empty">{t('journey.corpus.noMatch')}</p>}
      </div>
    </section>
  );
}

export default function JourneyPage({ onNavigate }: JourneyPageProps) {
  const { t } = useGlossary();
  const { user, profile } = useAuth();
  const isAuthenticated = !!user && !!profile;
  const [progress, setProgress] = useState<JourneyProgress | null>(null);
  const [query, setQuery] = useState('');
  const [searchResult, setSearchResult] = useState<LinguisticSearchResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!profile) {
      setProgress(null);
      return;
    }
    setProgress(startJourney(profile.uid));
  }, [profile?.uid]);

  useEffect(() => {
    setSearchLoading(true);
    searchLinguistics('')
      .then((result) => {
        setSearchResult(result);
        setSearchError('');
      })
      .catch((err) => {
        console.error('Journey linguistic search failed:', err);
        setSearchError(t('journey.error.loadCorpus'));
      })
      .finally(() => setSearchLoading(false));
  }, []);

  const group = useMemo(() => {
    if (!profile) return null;
    return loadSangatiGroup(profile.uid);
  }, [profile?.uid]);

  const consentDone = !!getConsentRecord();
  const profileDone = !!profile && Boolean(
    profile.alias.trim() &&
    profile.age.trim() &&
    profile.pincode.trim() &&
    profile.studyAreas.trim() &&
    profile.availability.length
  );
  const discoveryDone = !!profile?.isDiscoverable;
  const sangatiDone = !!group;
  const consentReady = consentDone;
  const profileReady = profileDone;
  const discoveryReady = discoveryDone;
  const sangatiReady = sangatiDone;
  const coreReady = consentDone && profileDone && sangatiDone;

  const completedCount = [consentReady, profileReady, discoveryReady, sangatiReady].filter(Boolean).length;
  const stepsTotal = 4;

  const handleSearch = async (e?: FormEvent) => {
    e?.preventDefault();
    setSearchLoading(true);
    setSearchError('');
    try {
      const result = await searchLinguistics(query, 10);
      setSearchResult(result);
      if (isAuthenticated && profile && query.trim() && result.count > 0) {
        const next = markJourneyStep(profile.uid, 'root-search', { lastQuery: query.trim(), lastRoute: '/journey' });
        setProgress(next);
      }
    } catch (err) {
      console.error('Journey search failed:', err);
      setSearchError(t('journey.error.search'));
    } finally {
      setSearchLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="journey-page">
        <header className="journey-hero">
          <div className="journey-eyebrow">{t('journey.eyebrow')}</div>
          <h1>{t('journey.title.guest')}</h1>
          <p>{t('journey.guest.body')}</p>
        </header>
        <button className="journey-back" onClick={() => onNavigate('/')}>{t('journey.button.home')}</button>
        <CorpusSection
          t={t}
          query={query}
          searchResult={searchResult}
          searchLoading={searchLoading}
          searchError={searchError}
          onQueryChange={setQuery}
          onSearch={handleSearch}
        />
      </div>
    );
  }

  const markAndNavigate = (stepId: string, path: string) => {
    const next = markJourneyStep(profile.uid, stepId, { lastRoute: path });
    setProgress(next);
    onNavigate(path);
  };

  const markJourneyDone = () => {
    const next = completeJourney(profile.uid, { lastRoute: '/journey' });
    setProgress(next);
    setNotice(t('journey.notice.complete'));
    setTimeout(() => setNotice(''), 2500);
  };

  return (
    <div className="journey-page">
      <header className="journey-hero">
        <div className="journey-eyebrow">{t('journey.eyebrow')}</div>
        <h1>{t('journey.title.main')}</h1>
        <p>
          {t('journey.body')}
        </p>
        <div className="journey-hero-meta">
          <span>{t('journey.meta.progress', { done: completedCount, total: stepsTotal })}</span>
          <span>{t('journey.meta.marked', { count: progress?.completedSteps.length ?? 0 })}</span>
          <span>{progress?.completedAt ? t('journey.meta.complete') : t('journey.meta.active')}</span>
          <span>{profile.profileKind === 'knowledge_provider' ? t('journey.meta.providerFlow') : t('journey.meta.learnerFlow')}</span>
        </div>
        <div className="journey-hero-actions">
          <button className="journey-btn journey-btn--ghost" onClick={() => onNavigate('/')}>{t('journey.button.home')}</button>
          <button className="journey-btn journey-btn--ghost" onClick={() => onNavigate('/profile')}>{t('journey.button.profile')}</button>
          <button className="journey-btn journey-btn--ghost" onClick={() => onNavigate('/sangati')}>{t('journey.button.sangati')}</button>
          <button className="journey-btn journey-btn--primary" onClick={() => onNavigate('/studio')}>{t('journey.button.studio')}</button>
        </div>
      </header>

      {notice && <div className="journey-toast">{notice}</div>}

      <section className="journey-summary">
        <div className="journey-summary-card">
            <strong>{t('journey.summary.standards.title')}</strong>
            <p>{t('journey.summary.standards.body')}</p>
          </div>
          <div className="journey-summary-card">
            <strong>{t('journey.summary.sangati.title')}</strong>
            <p>{group ? t('journey.summary.sangati.ready', { name: group.name }) : t('journey.summary.sangati.none')}</p>
          </div>
          <div className="journey-summary-card">
            <strong>{t('journey.summary.studio.title')}</strong>
            <p>{t('journey.summary.studio.body')}</p>
          </div>
        </section>

      <section className="journey-grid">
        <StepCard
          title={t('journey.step.consent.title')}
          body={t('journey.step.consent.body')}
          done={consentReady}
          actionLabel={consentReady ? t('journey.step.consent.done') : t('journey.step.consent.action')}
          onAction={() => (consentReady ? markAndNavigate('consent', '/') : onNavigate('/'))}
          muted={consentReady ? t('journey.step.consent.mutedDone') : t('journey.step.consent.mutedOpen')}
          t={t}
        />
        <StepCard
          title={t('journey.step.profile.title')}
          body={t('journey.step.profile.body')}
          done={profileReady}
          actionLabel={t('journey.step.profile.action')}
          onAction={() => markAndNavigate('profile', '/profile')}
          t={t}
        />
        <StepCard
          title={t('journey.step.discovery.title')}
          body={t('journey.step.discovery.body')}
          done={discoveryReady}
          actionLabel={profile.isDiscoverable ? t('journey.step.discovery.view') : t('journey.step.discovery.enable')}
          onAction={() => markAndNavigate('discovery', '/discovery')}
          muted={profile.isDiscoverable ? t('journey.step.discovery.mutedOn') : t('journey.step.discovery.mutedOff')}
          t={t}
        />
        <StepCard
          title={t('journey.step.sangati.title')}
          body={t('journey.step.sangati.body')}
          done={sangatiReady}
          actionLabel={sangatiDone ? t('journey.step.sangati.open') : t('journey.step.sangati.create')}
          onAction={() => markAndNavigate('sangati', '/sangati')}
          muted={group ? group.name : t('journey.step.sangati.muted')}
          t={t}
        />
      </section>

      <CorpusSection
        t={t}
        query={query}
        searchResult={searchResult}
        searchLoading={searchLoading}
        searchError={searchError}
        onQueryChange={setQuery}
        onSearch={handleSearch}
      />

      <section className="journey-handoff">
        <div>
          <div className="journey-corpus-eyebrow">{t('journey.handoff.eyebrow')}</div>
          <h2>{t('journey.handoff.title')}</h2>
          <p>
            {t('journey.handoff.body')}
          </p>
        </div>
        <div className="journey-handoff-visual" aria-hidden="true">
          <svg viewBox="0 0 620 130" className="journey-handoff-svg">
            <defs>
              <linearGradient id="journey-studio-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#1f7a5d" />
                <stop offset="100%" stopColor="#2458a6" />
              </linearGradient>
            </defs>
            <path d="M55 65 C145 18, 215 18, 290 65 S 435 112, 515 65 S 580 35, 595 65" fill="none" stroke="url(#journey-studio-gradient)" strokeWidth="10" strokeLinecap="round" />
            <circle cx="55" cy="65" r="15" fill="#fff" stroke="#1f7a5d" strokeWidth="4" />
            <circle cx="595" cy="65" r="15" fill="#fff" stroke="#2458a6" strokeWidth="4" />
            <text x="55" y="98" textAnchor="middle" className="journey-handoff-svg-label">वाद</text>
            <text x="595" y="98" textAnchor="middle" className="journey-handoff-svg-label">Studio</text>
          </svg>
          <div className="journey-handoff-visual-caption">प्रोफ़ाइल → वरीयताएँ → परिचय वीडियो</div>
        </div>
        <div className="journey-handoff-actions">
          <button className="journey-btn journey-btn--ghost" onClick={() => onNavigate('/studio')}>{t('journey.button.studioOpen')}</button>
          <button className="journey-btn journey-btn--primary" onClick={markJourneyDone} disabled={!coreReady}>
            {progress?.completedAt ? t('journey.button.complete') : t('journey.button.finish')}
          </button>
        </div>
      </section>
    </div>
  );
}
